import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift, Copy, ArrowUpRight, EnvelopeSimple, CheckCircle, Clock, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-only "Comp" page — generate personalised Stripe Checkout links with a
 * promo code pre-applied for any supplier email.
 *
 * Flow: admin types supplier email → picks plan + promo → backend mints the
 * Stripe session → admin gets a copy/share-ready link to forward.
 */
const AdminCompPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [promoCodes, setPromoCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);

  const [supplierEmail, setSupplierEmail] = useState('');
  const [plan, setPlan] = useState('annual');
  const [promoCode, setPromoCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/comp/history?limit=50`);
      setHistory(res.data?.items || []);
    } catch {
      // silent — non-critical
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Gate access — push non-admins back to dashboard
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/admin/comp');
      return;
    }
    if (!user.is_admin) {
      toast.error('Admin only');
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Load active Stripe promo codes
  useEffect(() => {
    if (!user?.is_admin) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/comp/promo-codes`);
        const codes = res.data?.codes || [];
        setPromoCodes(codes);
        if (codes.length > 0) setPromoCode(codes[0].code);
      } catch (e) {
        toast.error('Failed to load promo codes');
      } finally {
        setLoadingCodes(false);
      }
    })();
    fetchHistory();
  }, [user, fetchHistory]);

  const generate = async (e) => {
    e?.preventDefault();
    if (!supplierEmail || !promoCode) {
      toast.error('Fill in both email and promo code');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/comp/studio-pass-link`, {
        plan,
        supplier_email: supplierEmail,
        promo_code: promoCode,
        origin_url: window.location.origin,
      });
      setResult(res.data);
      toast.success(`Link minted for ${res.data.supplier?.email}`);
      fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mint link — try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      toast.success('Checkout link copied');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const buildMailto = (item, url, mode = 'first') =>
    `mailto:${encodeURIComponent(item.supplier_email || '')}?subject=${encodeURIComponent(`Your free EventDrop Studio Pass — ${item.plan === 'annual' ? 'Annual' : 'Monthly'}`)}&body=${encodeURIComponent(
      `Hey${item.supplier_name ? ` ${item.supplier_name.split(' ')[0]}` : ''},\n\n` +
        (mode === 'resend'
          ? `Just nudging this back to the top of your inbox — here's your free EventDrop Studio Pass (${item.plan}). Tap the link below, confirm your email, and you're activated.\n\n`
          : `As promised — here's your free EventDrop Studio Pass (${item.plan}). Tap the link below, confirm your email, and you're activated.\n\n`) +
        `${url}\n\n` +
        `Any questions just reply to this email. — Craig`,
    )}`;

  const [resendingId, setResendingId] = useState(null);
  const handleResend = async (item) => {
    setResendingId(item.session_id);
    try {
      const res = await axios.post(`${API}/admin/comp/resend/${item.session_id}`, {
        origin_url: window.location.origin,
      });
      const url = res.data?.url;
      if (!url) throw new Error('no url');
      // Open mail client with a warm "nudge" message + fresh link
      window.location.href = buildMailto(
        { ...item, supplier_email: res.data.supplier?.email || item.supplier_email },
        url,
        'resend',
      );
      toast.success('Fresh link minted — opening your mail client');
      fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not re-mint link');
    } finally {
      setResendingId(null);
    }
  };

  const mailtoHref = result
    ? buildMailto(
        {
          supplier_email: result.supplier?.email,
          supplier_name: result.supplier?.name,
          plan: result.plan,
        },
        result.url,
      )
    : '';

  if (authLoading || !user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1014] text-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#B89762]/40 border-t-transparent" />
      </div>
    );
  }

  const relTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = Math.max(0, Date.now() - d.getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const StatusBadge = ({ item }) => {
    if (item.subscription_active) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300">
          <CheckCircle size={10} weight="fill" /> ACTIVE
        </span>
      );
    }
    if (item.payment_status === 'paid' || item.status === 'complete') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300">
          <CheckCircle size={10} weight="fill" /> REDEEMED
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-300">
        <Clock size={10} weight="bold" /> AWAITING
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="admin-comp-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate('/admin')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-6"
          data-testid="admin-comp-back"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Admin
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #F59E0B, #DC2626)' }}
          >
            <Gift size={16} weight="fill" className="text-white" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300">
            Admin · Comp Studio Pass
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3">
          Mint a free Studio Pass.
        </h1>
        <p className="text-base text-gray-400 max-w-xl mb-10">
          Generate a personalised Stripe Checkout link with a promo code pre-applied.
          Forward it to the supplier — they confirm their email and get activated. No card needed when the code is 100%-off.
        </p>

        {/* Form */}
        <form
          onSubmit={generate}
          className="rounded-2xl p-6 mb-6"
          style={{
            background: 'linear-gradient(180deg, #161A36, #15161B)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
          }}
        >
          <label className="block text-xs uppercase tracking-[0.18em] font-bold text-gray-400 mb-2">
            Supplier email
          </label>
          <input
            type="email"
            value={supplierEmail}
            onChange={(e) => setSupplierEmail(e.target.value)}
            placeholder="supplier@studio.com"
            required
            className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B89762]/40 transition-colors mb-5"
            data-testid="comp-supplier-email"
          />

          <label className="block text-xs uppercase tracking-[0.18em] font-bold text-gray-400 mb-2">
            Plan
          </label>
          <div className="grid grid-cols-2 gap-2 mb-5" role="radiogroup">
            {[
              { id: 'annual', label: 'Annual · £299/yr' },
              { id: 'monthly', label: 'Monthly · £22.99/mo' },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPlan(p.id)}
                role="radio"
                aria-checked={plan === p.id}
                className={`py-2.5 rounded-xl text-sm font-bold transition-all ${
                  plan === p.id
                    ? 'text-white border border-[#B89762]/40 bg-[#C29D52]/10'
                    : 'text-gray-400 border border-white/10 hover:bg-white/5'
                }`}
                data-testid={`comp-plan-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <label className="block text-xs uppercase tracking-[0.18em] font-bold text-gray-400 mb-2">
            Promo code
          </label>
          {loadingCodes ? (
            <div className="text-sm text-gray-500 mb-5">Loading codes…</div>
          ) : promoCodes.length === 0 ? (
            <div className="text-sm text-amber-300 mb-5" data-testid="comp-no-codes">
              No active Stripe promo codes available. Create one in the Stripe dashboard first.
            </div>
          ) : (
            <select
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B89762]/40 transition-colors mb-5 appearance-none"
              data-testid="comp-promo-select"
            >
              {promoCodes.map((c) => (
                <option key={c.id} value={c.code} className="bg-[#15161B]">
                  {c.code} · {c.percent_off ? `${c.percent_off}% off` : `£${(c.amount_off || 0) / 100} off`} · {c.duration}
                </option>
              ))}
            </select>
          )}

          <button
            type="submit"
            disabled={submitting || !supplierEmail || !promoCode || promoCodes.length === 0}
            className="w-full py-3 rounded-xl font-bold text-white text-sm transition-all hover:scale-[1.015] disabled:opacity-50 disabled:hover:scale-100"
            style={{ background: 'linear-gradient(135deg, #F59E0B, #DC2626)' }}
            data-testid="comp-generate"
          >
            {submitting ? 'Minting link…' : 'Generate comp link'}
          </button>
        </form>

        {/* Result */}
        {result && (
          <div
            className="rounded-2xl p-6"
            style={{
              background: 'rgba(34, 197, 94, 0.05)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
            }}
            data-testid="comp-result"
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={18} weight="fill" className="text-emerald-400" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-300">
                Link ready · {result.plan} · {result.promo_code}
              </span>
            </div>
            <div className="text-sm text-gray-300 mb-4">
              Minted for <strong className="text-white">{result.supplier?.email}</strong>
              {result.supplier?.name ? ` (${result.supplier.name})` : ''}.
            </div>
            <div className="bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-[11px] font-mono text-gray-300 break-all mb-4">
              {result.url}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={copyLink}
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border border-white/15 hover:bg-white/5 transition-all"
                data-testid="comp-copy"
              >
                <Copy size={14} weight="bold" /> Copy link
              </button>
              <a
                href={mailtoHref}
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border border-white/15 hover:bg-white/5 transition-all"
                data-testid="comp-email"
              >
                <EnvelopeSimple size={14} weight="bold" /> Email it
              </a>
              <a
                href={result.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="comp-open"
              >
                Open <ArrowUpRight size={14} weight="bold" />
              </a>
            </div>
          </div>
        )}

        {/* Comp History */}
        <div className="mt-12" data-testid="comp-history">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-400">
                Comp History
              </span>
              <h2 className="text-2xl font-black tracking-tight">Recent comp links</h2>
            </div>
            <button
              onClick={fetchHistory}
              className="flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors"
              data-testid="comp-history-refresh"
              type="button"
            >
              <ArrowsClockwise size={12} weight="bold" /> Refresh
            </button>
          </div>

          {loadingHistory ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : history.length === 0 ? (
            <div
              className="rounded-2xl p-6 text-center text-sm text-gray-500"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px dashed rgba(255,255,255,0.08)',
              }}
              data-testid="comp-history-empty"
            >
              No comp links minted yet. Generate one above and it'll appear here.
            </div>
          ) : (
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div className="divide-y divide-white/5">
                {history.map((item) => {
                  const awaiting =
                    !item.subscription_active &&
                    item.payment_status !== 'paid' &&
                    item.status !== 'complete';
                  return (
                    <div
                      key={item.session_id}
                      className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
                      data-testid={`comp-history-row-${item.session_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-white text-sm truncate">
                            {item.supplier_email || item.supplier_user_id}
                          </span>
                          <StatusBadge item={item} />
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="capitalize">{item.plan}</span>
                          <span>·</span>
                          <span className="text-amber-300">{item.promo_code}</span>
                          <span>·</span>
                          <span>{relTime(item.created_at)}</span>
                          {item.supplier_name && (
                            <>
                              <span>·</span>
                              <span>{item.supplier_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {awaiting && (
                        <button
                          onClick={() => handleResend(item)}
                          disabled={resendingId === item.session_id}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold border border-amber-400/40 text-amber-200 hover:bg-amber-400/10 transition-all disabled:opacity-50"
                          data-testid={`comp-resend-${item.session_id}`}
                        >
                          <EnvelopeSimple size={12} weight="bold" />
                          {resendingId === item.session_id ? 'Re-minting…' : 'Resend email'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCompPage;
